import { GREEN } from "@finway-group/shared/lib/consts"
import { PriceIntervalEnum } from "@finway-group/shared/lib/models"
import { TenantTierEnum } from "@finway-group/shared/lib/models/featureFlags/tenantTier.enum"
import { Button, Card, Col, Divider } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { DoneIcon } from "Components/icons"
import TierIcon, { TenantTierPaidEnum } from "Components/icons/tierIcon"
import UndoneIcon from "Components/icons/undoneIcon"
import PriceLabel from "Components/priceLabel"
import { PaymentProductUI } from "Shared/services/paymentSettings.service"

interface PaymentSubscriptionUpdateTierColInterface {
    plans: Array<PaymentProductUI>
    tier: TenantTierPaidEnum
    onClick: () => void
}

const PaymentSubscriptionUpdateTierCol: React.FC<PaymentSubscriptionUpdateTierColInterface> = ({ plans, tier, onClick }) => {
    const { t } = useTranslation()

    const plan = plans.filter((e) => e.metadata.tier === tier).sort((a, b) => Number(a.metadata.units) - Number(b.metadata.units))?.[0]
    const price = plan?.prices?.filter((e) => e.interval === PriceIntervalEnum.MONTHLY)?.[0]

    const renderLabel = (icon: JSX.Element, text: string) => (
        <div className="flex">
            <div>{icon}</div>
            <div className="ml-5">{t(text)}</div>
        </div>
    )

    const getDataForTier = (tier: TenantTierPaidEnum) => {
        switch (tier) {
            case TenantTierEnum.TIER_1:
                return {
                    getFeatureLabel: (name: string) => {
                        switch (name) {
                            case "label:payment_settings.tier.reporting_cc_groups":
                            case "label:payment_settings.tier.trm_management":
                            case "label:payment_settings.tier.integration_csv_custom":
                            case "label:payment_settings.tier.other_multi_tenancy":
                                return renderLabel(<UndoneIcon />, name)
                        }
                        return renderLabel(<DoneIcon />, name)
                    },
                }
            case TenantTierEnum.TIER_2:
                return {
                    getFeatureLabel: (name: string) => renderLabel(<DoneIcon />, name),
                }
        }
    }

    const tierData = getDataForTier(tier)

    return (
        <Col span={11}>
            <Card className="px-10">
                <div className="flex justify-center mb-10">
                    <h1 className="flex items-center space-x-10">
                        <TierIcon tier={tier} /> <span className="text-3xl ml-5">{t(`label:payment_settings.tier.${tier}.title`)}</span>
                    </h1>
                </div>
                <p className="flex justify-center">{t(`label:payment_settings.tier.${tier}.description`)}</p>
                <Divider />
                <p className="flex justify-center mb-10 items-baseline">
                    {price && (
                        <>
                            {t(`label:payment_settings.tier.starting_at`)}
                            <span className="font-bold text-4xl mx-3" style={{ color: GREEN }}>
                                <PriceLabel value={(price.amount ?? 1) / 100} currency={price?.currency} />
                            </span>
                            {t(`label:payment_settings.tier.per_mo`)}
                        </>
                    )}
                </p>
                <p className="flex justify-center mb-10">
                    {t(`label:payment_settings.tier.up_to`)}
                    <span className="font-bold mx-3">{plan.metadata.units}</span>
                    {t(`label:payment_settings.tier.monthly_invoices`)}
                </p>
                <Button onClick={onClick} className="mb-20 w-full ant-btn btn-highlight-green-active">
                    {t(`label:payment_settings.tier.${tier}.get_button`)}
                </Button>
                <h3 style={{ color: GREEN }}>{t(`label:payment_settings.tier.features_included`)}</h3>

                <p className="font-bold mt-10">{t(`label:payment_settings.tier.expense_features`)}</p>
                {tierData.getFeatureLabel(`label:payment_settings.tier.expense_purchase_requests`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.expense_invoices_and_ocr`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.expense_workflows`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.expense_employee_and_vendor_rules`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.expense_duplicate_check`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.expense_archiving`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.expense_inital_account_assignment`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.expense_splits`)}

                <p className="font-bold mt-10">{t(`label:payment_settings.tier.card_banking_features`)}</p>
                {tierData.getFeatureLabel(`label:payment_settings.tier.cards_virtual`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.cards_physical`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.card_modes`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.banking_connection`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.banking_batch_payment`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.banking_sepa_xml`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.banking_transaction_matching`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.banking_google_and_apple_pay`)}

                <p className="font-bold mt-10">{t(`label:payment_settings.tier.reporting_features`)}</p>
                {tierData.getFeatureLabel(`label:payment_settings.tier.reporting_budgets`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.reporting_cc_groups`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.reporting_std`)}

                <p className="font-bold mt-10">{t(`label:payment_settings.tier.integration_features`)}</p>
                {tierData.getFeatureLabel(`label:payment_settings.tier.integtation_datev_export`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.integration_datev_dc`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.integration_gmi`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.integration_csv_std`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.integration_csv_custom`)}

                <p className="font-bold mt-10">{t(`label:payment_settings.tier.reimbursement_features`)}</p>
                {tierData.getFeatureLabel(`label:payment_settings.tier.reimbursement_std`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.reimbursement_trm`)}

                <p className="font-bold mt-10">{t(`label:payment_settings.tier.liquidity_features`)}</p>
                {tierData.getFeatureLabel(`label:payment_settings.tier.liquidity_v1`)}

                <p className="font-bold mt-10">{t(`label:payment_settings.tier.other_features`)}</p>
                {tierData.getFeatureLabel(`label:payment_settings.tier.other_vendor_management`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.other_billing`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.other_mobile_app`)}
                {tierData.getFeatureLabel(`label:payment_settings.tier.other_multi_tenancy`)}
            </Card>
        </Col>
    )
}

export default PaymentSubscriptionUpdateTierCol
